import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Alert,
  Divider,  Grid,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Dialog,
  TextField,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ViewList as ViewListIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import {
  useRunJob,
  useArchiveReview,
  useCancelReview,
  useDownloadReview,
} from '../../../services/bulkPricingReview/bulkPricingReview.service';
import { ReviewObjectDto } from '../../../services/bulkPricingReview/bulkPricingReview.types';
import { useUser } from '../../../services/auth/auth.service';
import { usePrioritizedVariantInfo } from '../../../services/variants';
import { usePrioritizedPatches } from '../../../services/patches';
import { showToast } from '../../../components/Toast/Toast';
import { ToastTypes } from '../../../components/Toast/Toast.types';

interface BulkReviewDetailsProps {
  review: ReviewObjectDto;
  onBack: () => void;
  onRefresh?: () => void;
  onActionCompleted?: () => void; // Callback when an action (run/cancel) is completed
  // Remove onStepChange since we stay in the same step
}

const BulkReviewDetails: React.FC<BulkReviewDetailsProps> = ({
  review,
  onBack,
  onRefresh,
  onActionCompleted,
}) => {  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [fileContent, setFileContent] = useState<string>('');
  const [parsedOperations, setParsedOperations] = useState<any[]>([]);
  const [viewMode, setViewMode] = useState<'raw' | 'formatted'>('formatted');
  const [loadingContent, setLoadingContent] = useState(false);
    // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [10, 25, 50];  // Filter state
  const [filters, setFilters] = useState({
    productName: '',
    storeNumber: '',
    minPrice: '',
    maxPrice: ''
  });
  // Mutations
  const runJobMutation = useRunJob();
  const archiveMutation = useArchiveReview();
  const cancelMutation = useCancelReview();
  const downloadMutation = useDownloadReview();

  // Get current user for userId
  const { data: userData } = useUser(true);
  const currentUser = userData?.data;

  // Extract franchise code from file path
  const extractFranchiseFromPath = (path: string): string => {
    const parts = path.split('/');
    return parts.length >= 2 ? parts[1] : '';
  };
  const franchiseCode = extractFranchiseFromPath(review.key);  const fileName = review.key.split('/').pop() || review.key;  // Extract variant and patch IDs needed for current page (for prioritized loading)
  const currentPageVariantIds = useMemo(() => {
    return parsedOperations
      .slice((currentPage - 1) * pageSize, currentPage * pageSize)
      .map(op => op.setOperations?.setVariantPrice?.variantId)
      .filter(Boolean);
  }, [parsedOperations, currentPage, pageSize]);

  const currentPagePatchIds = useMemo(() => {
    return parsedOperations
      .slice((currentPage - 1) * pageSize, currentPage * pageSize)
      .flatMap(op => op.patchIds || [])
      .filter(Boolean);
  }, [parsedOperations, currentPage, pageSize]);  // Use prioritized loading for better performance
  const variantData = usePrioritizedVariantInfo(currentPageVariantIds);
  const patchData = usePrioritizedPatches(
    franchiseCode,
    currentPagePatchIds,
    'setprodprices'
  );  // Create optimized loading states
  const isPriorityDataLoading = variantData.isLoading || patchData.isLoading;
  
  // Use appropriate data based on what's available
  const variantMap = variantData.variantMap;
  const patchLookupMap = patchData.data;
  
  // Check if we have all the data we need for current page
  const hasCurrentPageVariantData = currentPageVariantIds.length === 0 || 
    currentPageVariantIds.every(id => variantMap.has(id));
  const hasCurrentPagePatchData = currentPagePatchIds.length === 0 || 
    (patchLookupMap && currentPagePatchIds.every(id => patchLookupMap[id]));
  
  // Show loading only when we're actively loading priority data AND missing current page data
  // or when we have no variant/patch data at all for current page
  const isLoadingCurrentPageData = (isPriorityDataLoading && (!hasCurrentPageVariantData || !hasCurrentPagePatchData)) ||
    (!variantData.hasPriorityData && currentPageVariantIds.length > 0) ||
    (!patchData.data && currentPagePatchIds.length > 0);

  // Reset to first page when operations change
  useEffect(() => {
    setCurrentPage(1);
  }, [parsedOperations.length]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Optimization: Cancel any ongoing background requests when component unmounts
  // or when page changes to avoid unnecessary network calls
  useEffect(() => {
    // This effect will run cleanup when dependencies change
    return () => {
      // The react-query hooks will handle their own cleanup
      // but we can add any additional logic here if needed
    };
  }, [currentPage, parsedOperations]);

  // Download function
  const handleDownload = useCallback(() => {
    const blob = new Blob([fileContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);  }, [fileContent, fileName]);  // Helper function to get variant display information
  const getVariantDisplayInfo = useCallback((variantId: string) => {
    const variantInfo = variantMap.get(variantId);
    
    if (!variantInfo) {
      const shortId = variantId.length > 8 ? `...${variantId.slice(-8)}` : variantId;
      return {
        displayName: shortId,
        productName: shortId,
        variantDescription: 'Product not found',
        price: 0,
        categories: [],
        options: [],
        isUnknown: true
      };
    }
    
    return {
      displayName: variantInfo.fullDescription,
      productName: variantInfo.productName,
      variantDescription: variantInfo.variantDescription,
      price: variantInfo.price,
      categories: variantInfo.categories,
      options: variantInfo.options || [],
      isUnknown: false
    };
  }, [variantMap]);// Helper function to get patch display information (map patch IDs to store numbers)
  const getPatchDisplayInfo = useCallback((patchIds: string[]) => {
    if (!patchIds || patchIds.length === 0) {
      return {
        storeNumbers: [],
        isLoading: false
      };
    }    if (!patchLookupMap) {
      return {
        storeNumbers: patchIds.map(id => {
          const shortId = id.length > 8 ? `...${id.slice(-8)}` : id;
          return shortId;
        }),
        isLoading: isLoadingCurrentPageData
      };
    }

    const storeNumbers: string[] = [];
    const unknownPatches: string[] = [];
    
    patchIds.forEach(patchId => {
      const patchInfo = patchLookupMap[patchId];
      if (patchInfo?.storeNumber) {
        if (!storeNumbers.includes(patchInfo.storeNumber)) {
          storeNumbers.push(patchInfo.storeNumber);
        }
      } else {
        unknownPatches.push(patchId);
      }
    });    // Combine known store numbers with unknown patch info
    const allDisplayItems = [
      ...storeNumbers,
      ...unknownPatches.map(id => {
        const shortId = id.length > 8 ? `...${id.slice(-8)}` : id;
        return shortId;
      })
    ];

    return {
      storeNumbers: allDisplayItems.length > 0 ? allDisplayItems : [`No stores found for ${patchIds.length} patch(es)`],
      isLoading: isLoadingCurrentPageData
    };
  }, [patchLookupMap, isLoadingCurrentPageData]);
  const handleRunJob = useCallback(async () => {
    if (!currentUser?.loginId) {
      showToast({
        label: 'User not authenticated',
        id: 'auth-error',
        toastType: ToastTypes.Error,
      });
      return;
    }

    try {
      await runJobMutation.mutateAsync({
        userId: currentUser.loginId,
        fileName,
        franchiseCode,
      });
      showToast({
        label: 'Job started successfully',
        id: 'job-start-success',
        toastType: ToastTypes.Success,
      });
      
      onRefresh?.();
      onActionCompleted?.();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to start job';
      showToast({
        label: errorMessage,
        id: 'job-start-error',
        toastType: ToastTypes.Error,
      });    }
  }, [fileName, franchiseCode, runJobMutation, onRefresh, currentUser]);const handleCancel = useCallback(async () => {
    try {
      if (!currentUser?.loginId) {
        showToast({
          label: 'User authentication required to cancel job',
          id: 'cancel-auth-error',
          toastType: ToastTypes.Error,
        });
        return;
      }      console.log('Attempting to cancel job with:', { filePath: review.key });
        await cancelMutation.mutateAsync({
        filePath: review.key,
        userId: currentUser.loginId,
      });
        showToast({
        label: 'Job cancelled successfully',
        id: 'cancel-success',
        toastType: ToastTypes.Success,
      });
      setShowCancelDialog(false);
      onRefresh?.();
      onActionCompleted?.();
      // Stay in review details after canceling
    } catch (error: any) {
      console.error('Cancel job error details:', error);
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Failed to cancel job';
      showToast({
        label: errorMessage,
        id: 'cancel-error',
        toastType: ToastTypes.Error,
      });
    }
  }, [fileName, franchiseCode, cancelMutation, onRefresh]);  const handleArchive = useCallback(async () => {
    try {      await archiveMutation.mutateAsync({
        filePath: review.key,
      });
        showToast({
        label: 'Job archived successfully',
        id: 'archive-success',
        toastType: ToastTypes.Success,
      });
      
      onRefresh?.();
      onActionCompleted?.();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to archive job';
      showToast({
        label: errorMessage,
        id: 'archive-error',
        toastType: ToastTypes.Error,
      });
    }
  }, [fileName, franchiseCode, archiveMutation, onRefresh, onActionCompleted]);

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'processing':
      case 'in_progress':
        return 'primary';
      case 'pending':
        return 'info';
      case 'canceled':
      case 'cancelled':
        return 'warning';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };  const handleBack = useCallback(() => {
    // Just go back to list view within the same step
    onBack();
  }, [onBack]);

  const canRunJob = review.status === 'pending';
  const canCancelJob = review.status === 'pending' || review.status === 'processing';
  const canArchiveJob = review.status === 'completed' || review.status === 'failed' || review.status === 'canceled';  // Remove step progression logic since we stay within Step 1
  // useEffect(() => {
  //   // Don't change the step - keep it at step 1 but show review details
  //   // onStepChange?.(1); // Move to step 2 (index 1)
  // }, [onStepChange]);  // Load file content on mount
  useEffect(() => {
    const loadContent = async () => {
      setLoadingContent(true);
      try {          const response = await downloadMutation.mutateAsync({
          fileName: fileName,          franchiseCode: franchiseCode,
        });
        
        // Handle both string and object responses
        let content: string;
        let operations: any[] = [];
        
        if (typeof response.data === 'string') {
          content = response.data;
          try {
            operations = JSON.parse(content);
          } catch (parseError) {
            // If it's not valid JSON, treat as plain text
            console.warn('Content is not valid JSON:', parseError);
          }
        } else if (response.data && typeof response.data === 'object') {
          // Convert object to formatted JSON string
          content = JSON.stringify(response.data, null, 2);
          operations = Array.isArray(response.data) ? response.data : [response.data];
        } else {
          content = 'No content available';
        }
        
        setFileContent(content);
        setParsedOperations(Array.isArray(operations) ? operations : []);
        
      } catch (error: any) {
        console.error('Failed to load file content:', error);
        setFileContent('Failed to load file content. Please try again.');
        setParsedOperations([]);
      } finally {
        setLoadingContent(false);
      }
    };

    loadContent();
  }, [review.key]); // Only depend on review.key, not downloadMutation

  // Apply filters to operations
  const filteredOperations = useMemo(() => {
    return parsedOperations.filter(operation => {
      // Get operation details
      const variantId = operation.setOperations?.setVariantPrice?.variantId;
      const newPrice = operation.setOperations?.setVariantPrice?.authoringPrice?.amount || 0;
      const variantInfo = variantId ? getVariantDisplayInfo(variantId) : null;
      const patchDisplay = getPatchDisplayInfo(operation.patchIds || []);      // Apply filters      
      if (filters.productName && variantInfo) {
        const productName = variantInfo.productName.toLowerCase();
        if (!productName.includes(filters.productName.toLowerCase())) {
          return false;
        }
      }
      
      if (filters.storeNumber && patchDisplay.storeNumbers.length > 0) {
        const hasMatchingStore = patchDisplay.storeNumbers.some(storeNumber => 
          storeNumber.toLowerCase().includes(filters.storeNumber.toLowerCase())
        );
        if (!hasMatchingStore) {
          return false;
        }
      }
      
      if (filters.minPrice && newPrice < (parseFloat(filters.minPrice) * 100)) {
        return false;
      }
        if (filters.maxPrice && newPrice > (parseFloat(filters.maxPrice) * 100)) {
        return false;
      }
      
      return true;
    });
  }, [parsedOperations, filters, getVariantDisplayInfo, getPatchDisplayInfo]);

  // Update pagination calculations to use filtered operations
  const totalFilteredOperations = filteredOperations.length;
  const totalPages = Math.ceil(totalFilteredOperations / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentOperations = filteredOperations.slice(startIndex, endIndex);

  return (
    <Box>      {/* Header with Back Button */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          variant="outlined"
        >
          Back to List
        </Button>
        <Typography variant="h4">Review Job Details</Typography>
      </Box>

      {/* Job Details Card */}
      <Card sx={{ mb: 3 }}>
        <CardHeader 
          title="Job Information"
          subheader={`Review and manage this bulk pricing job`}
        />
        <Divider />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                File Name
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {fileName}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Franchise Code
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {franchiseCode}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Status
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={review.status}
                  color={getStatusColor(review.status)}
                  size="small"
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                File Size
              </Typography>
              <Typography variant="body1">
                {formatFileSize(review.size)}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Last Modified
              </Typography>
              <Typography variant="body1">
                {new Date(review.lastModified).toLocaleString()}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                S3 Key
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                {review.key}
              </Typography>
            </Grid>
          </Grid>        </CardContent>
      </Card>      {/* Operations Analysis Card */}
      {parsedOperations.length > 0 && (
        <Card sx={{ mb: 3 }}>          <CardHeader 
            title="Operations Analysis"
            subheader={`${parsedOperations.length} operations found${filteredOperations.length !== parsedOperations.length ? ` (${filteredOperations.length} filtered)` : ''}`}
            action={
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(_, newMode) => newMode && setViewMode(newMode)}
                  size="small"
                >
                  <ToggleButton value="formatted" aria-label="formatted view">
                    <ViewListIcon sx={{ mr: 1 }} />
                    Formatted
                  </ToggleButton>
                  <ToggleButton value="raw" aria-label="raw json">
                    <CodeIcon sx={{ mr: 1 }} />
                    Raw JSON
                  </ToggleButton>
                </ToggleButtonGroup>
                
                {loadingContent ? (
                  <CircularProgress size={24} />
                ) : (
                  <Button
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                    size="small"
                    variant="outlined"
                  >
                    Download
                  </Button>
                )
                }
              </Box>
            }
          />
          <Divider />          <CardContent>
            {viewMode === 'formatted' ? (
              <Box>                {/* Filters */}                <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>Filters</Typography>
                  <Grid container spacing={2}>                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Product Name"
                        value={filters.productName}
                        onChange={(e) => setFilters(prev => ({ ...prev, productName: e.target.value }))}
                        placeholder="Search products..."
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Store Number"
                        value={filters.storeNumber}
                        onChange={(e) => setFilters(prev => ({ ...prev, storeNumber: e.target.value }))}
                        placeholder="Store number..."
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Min Price ($)"
                        type="number"
                        value={filters.minPrice}
                        onChange={(e) => setFilters(prev => ({ ...prev, minPrice: e.target.value }))}
                        placeholder="0.00"
                        inputProps={{ step: "0.01", min: "0" }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Max Price ($)"
                        type="number"
                        value={filters.maxPrice}
                        onChange={(e) => setFilters(prev => ({ ...prev, maxPrice: e.target.value }))}
                        placeholder="999.99"
                        inputProps={{ step: "0.01", min: "0" }}
                      />
                    </Grid>
                  </Grid>                  {(filters.productName || filters.storeNumber || filters.minPrice || filters.maxPrice) && (
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        onClick={() => setFilters({ productName: '', storeNumber: '', minPrice: '', maxPrice: '' })}
                      >
                        Clear Filters
                      </Button>
                    </Box>
                  )}
                </Card>

                {/* Pagination Controls */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Page Size</InputLabel>
                    <Select
                      value={pageSize}
                      label="Page Size"
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      {pageSizeOptions.map((size) => (
                        <MenuItem key={size} value={size}>
                          {size} per page
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>                  <Typography variant="body2" color="text.secondary">
                    Showing {startIndex + 1}-{Math.min(endIndex, totalFilteredOperations)} of {totalFilteredOperations} operations
                  </Typography>                  
                  {/* Simple loading indicator */}
                  {isLoadingCurrentPageData && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="caption" color="text.secondary">
                        Loading data...
                      </Typography>
                    </Box>
                  )}
                </Box>                {/* Operations List */}
                {currentOperations.length === 0 ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {totalFilteredOperations === 0 ? 
                      'No operations match the current filters.' : 
                      'No operations on this page. Try adjusting the page size or navigate to a different page.'
                    }
                  </Alert>
                ) : (
                  currentOperations.map((operation, index) => {
                  // Get variant info for the accordion header
                  const variantId = operation.setOperations?.setVariantPrice?.variantId;
                  const variantInfo = variantId ? getVariantDisplayInfo(variantId) : null;
                  
                  return (
                    <Accordion key={`accordion-${startIndex}-${index}`} sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1">
                            Operation {startIndex + index + 1}                          </Typography>                          {variantInfo && !isLoadingCurrentPageData && (
                            <Chip 
                              label={variantInfo.isUnknown ? variantInfo.displayName : (variantInfo.displayName || variantInfo.productName)} 
                              size="small" 
                              color={variantInfo.isUnknown ? "warning" : "info"}
                              sx={{ maxWidth: 'none', flexShrink: 1 }}
                            />
                          )}{isLoadingCurrentPageData && (
                            <Chip 
                              label="Loading product info..." 
                              size="small" 
                              color="default"
                              sx={{ maxWidth: '300px' }}
                            />
                          )}                          {!variantInfo && !isLoadingCurrentPageData && variantId && (
                            <Chip 
                              label={variantId.length > 8 ? `...${variantId.slice(-8)}` : variantId} 
                              size="small" 
                              color="warning"
                              sx={{ maxWidth: '300px' }}
                            />
                          )}{operation.patchIds && (() => {
                            const patchDisplay = getPatchDisplayInfo(operation.patchIds);
                            const storeCount = patchDisplay.storeNumbers.length;
                            const displayText = patchDisplay.isLoading 
                              ? `Loading stores...`
                              : `${storeCount} Store${storeCount !== 1 ? 's' : ''}`;
                            
                            return (
                              <Chip 
                                label={displayText}
                                size="small" 
                                color={patchDisplay.isLoading ? "default" : "info"}
                                sx={{ maxWidth: '200px' }}
                              />
                            );
                          })()}</Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Store Numbers
                          </Typography>
                          {(() => {
                            const patchDisplay = getPatchDisplayInfo(operation.patchIds || []);
                            
                            if (patchDisplay.isLoading) {
                              return (
                                <Chip 
                                  label="Loading store numbers..." 
                                  variant="outlined" 
                                  size="small" 
                                  sx={{ mr: 1, mb: 1 }}
                                />
                              );
                            }                              return patchDisplay.storeNumbers.map((storeNumber: string, pIndex: number) => {                              // Determine chip color based on content
                              let chipColor: 'default' | 'primary' | 'secondary' | 'warning' | 'info' | 'success' | 'error' = 'default';
                              if (storeNumber.includes('...') || storeNumber.match(/^[a-f0-9]{8}$/)) {
                                // Unknown patch ID (shortened)
                                chipColor = 'warning';
                              } else if (storeNumber.includes('No stores found')) {
                                chipColor = 'error';
                              } else if (!isNaN(Number(storeNumber))) {
                                chipColor = 'primary'; // Valid store numbers
                              }
                              
                              return (
                                <Chip 
                                  key={pIndex} 
                                  label={storeNumber} 
                                  variant="outlined" 
                                  size="small" 
                                  color={chipColor}
                                  sx={{ mr: 1, mb: 1 }}
                                />
                              );
                            });
                          })()}</Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Operations
                          </Typography>
                          {operation.setOperations?.setVariantPrice && (() => {
                            const variantId = operation.setOperations.setVariantPrice.variantId;
                            const variantInfo = getVariantDisplayInfo(variantId);
                            const newPrice = operation.setOperations.setVariantPrice.authoringPrice?.amount;
                            
                            return (                              <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  <strong>Product:</strong> {variantInfo.productName}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  <strong>Variant ID:</strong> {variantId}
                                </Typography>
                                {variantInfo.options && variantInfo.options.length > 0 && (
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                      <strong>Options:</strong>
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                      {variantInfo.options.map((option: any, idx: number) => (
                                        <Chip
                                          key={idx}
                                          label={`${option.optionType}: ${option.optionValue}`}
                                          size="small"
                                          variant="outlined"
                                          sx={{ fontSize: '0.75rem' }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  <strong>New Price:</strong> ${((newPrice || 0) / 100).toFixed(2)}                                </Typography>                                {variantInfo.categories.length > 0 && (
                                  <Box>
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                      <strong>Categories:</strong>
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                      {variantInfo.categories.map((category: any, idx: number) => (
                                        <Chip
                                          key={idx}
                                          label={category.name}
                                          size="small"
                                          color="default"
                                          sx={{ fontSize: '0.75rem' }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>                                )}
                                {isLoadingCurrentPageData && (
                                  <Typography variant="caption" color="text.secondary">
                                    Loading product details...
                                  </Typography>
                                )}
                              </Box>
                            );
                          })()}                        </Grid>
                      </Grid>                      </AccordionDetails>
                  </Accordion>
                  );
                }))}

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={(_, page) => setCurrentPage(page)}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                )}
              </Box>) : (
              <TextField
                multiline
                fullWidth
                value={fileContent || ''}
                rows={8}
                InputProps={{
                  readOnly: true,
                  sx: {
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                  }
                }}
                sx={{ 
                  '& .MuiInputBase-root': {
                    backgroundColor: '#f5f5f5',
                  }
                }}
              />
            )}
          </CardContent>        </Card>
      )}

      {/* Actions Card */}
      <Card>
        <CardHeader 
          title="Available Actions"
          subheader="Choose an action to perform on this job"
        />
        <Divider />
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {canRunJob && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<PlayIcon />}
                onClick={handleRunJob}
                disabled={runJobMutation.isLoading}
                size="large"
              >
                {runJobMutation.isLoading ? 'Starting Job...' : 'Run Job'}
              </Button>
            )}
            
            {canCancelJob && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => setShowCancelDialog(true)}
                disabled={cancelMutation.isLoading}
                size="large"
              >
                Cancel Job
              </Button>
            )}
            
            {canArchiveJob && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleArchive}
                disabled={archiveMutation.isLoading}
                size="large"
              >
                {archiveMutation.isLoading ? 'Archiving...' : 'Archive Job'}
              </Button>
            )}
          </Box>
          
          {!canRunJob && !canCancelJob && !canArchiveJob && (
            <Alert severity="info">
              No actions are available for jobs with status "{review.status}".
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelDialog} onClose={() => setShowCancelDialog(false)}>
        <DialogTitle>Cancel Job</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel this job? This action cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            File: {fileName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Franchise: {franchiseCode}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelDialog(false)}>
            Keep Job
          </Button>
          <Button 
            onClick={handleCancel} 
            color="error" 
            variant="contained"
            disabled={cancelMutation.isLoading}
          >
            {cancelMutation.isLoading ? 'Cancelling...' : 'Cancel Job'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BulkReviewDetails;

